import { useCallback } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { FormikHelpers } from 'formik';
import Loading from '@/components/general/Loading';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import candidatesServices from '@/services/candidates';
import jobsServices from '@/services/jobs';
import { chakraExtendedTheme } from '@/styles/theme';
import { TechStackType, FetchApiIds } from '@/types';
import { scrollToFieldWithError } from '@/utils/formsValidation';
import NewJobFormComponent, { ValuesType, NewJobFormComponentType } from './NewJobForm.component';

export type NewJobFormType = Pick<
  NewJobFormComponentType,
  'onHideJobFormClick' | 'isFormDisabled' | 'onDirtyForm'
> & {
  onAddNewJob: (id: string) => void;
};

const NewJobForm = function ({
  onAddNewJob,
  onHideJobFormClick,
  isFormDisabled,
  onDirtyForm,
  ...props
}: NewJobFormType): JSX.Element {
  const toast = useToastContext();

  const createJobMutation = useApiSubmit<any, any>(jobsServices.createJob);

  const allTechStacksResponse = useApiFetch<TechStackType[], void>(
    FetchApiIds.TECH_STACKS,
    candidatesServices.getAllTechsStacks
  );

  const allPartnerAccountsResponse = useApiFetch(
    FetchApiIds.PARTNER_ACCOUNTS,
    jobsServices.getAllPartners
  );

  const techStacksOptions = (allTechStacksResponse.data ?? []).map((tech) => ({
    label: tech?.name,
    value: tech?.id,
  }));

  const partnerOptions = (allPartnerAccountsResponse.data ?? []).map((account) => ({
    label: account.name,
    value: account.id as string,
  }));

  const onSubmitForm = async (
    { techs, account, salaryRangeMax, salaryRangeMin, ...values }: ValuesType,
    actions: FormikHelpers<any>
  ): Promise<void> => {
    try {
      const jobCreated = await createJobMutation.mutateAsync({
        ...values,
        salaryRange: {
          max: salaryRangeMax,
          min: salaryRangeMin,
        },
        skillIds: techs,
        accountId: account,
      });

      toast.showSuccessFeedback('The job has been created.');
      actions.resetForm();
      onHideJobFormClick();
      onAddNewJob(jobCreated.id);
    } catch (err) {
      const defaultErrorMessage = 'There was a problem, please, try later.';
      const errorMessage = createJobMutation?.error?.response?.data?.message;
      toast.showErrorFeedback(errorMessage || defaultErrorMessage);
    }
  };

  const getTitle = useCallback((values) => {
    const { title } = values;
    return title ?? '';
  }, []);

  const getPartnerName = useCallback(
    (values) => {
      const { account } = values;

      const partnerFiltered = partnerOptions.find((partner) => partner.value === account);
      return partnerFiltered?.label ?? '';
    },
    [partnerOptions]
  );

  if (allTechStacksResponse.isLoading) {
    return <Loading fullPage />;
  }

  return (
    <ChakraProvider theme={chakraExtendedTheme}>
      <NewJobFormComponent
        techStacksOptions={techStacksOptions}
        partnerOptions={partnerOptions}
        scrollToFieldWithError={() => scrollToFieldWithError('new-job-form-area')}
        onDirtyForm={onDirtyForm}
        isFormDisabled={isFormDisabled}
        onHideJobFormClick={onHideJobFormClick}
        onSubmitForm={onSubmitForm}
        getPartnerName={getPartnerName}
        getTitle={getTitle}
        {...props}
      />
    </ChakraProvider>
  );
};

export default NewJobForm;
